import {
  get,
  isEmpty,
  map,
  trim,
  isUndefined,
  cloneDeep,
  isArray,
} from "lodash";
import moment from "moment";
import {
  FIREBASE_SOCIAL_LOGIN_MESSAGES,
  RESEND_CONNECTION_REQUEST_TIME_LIMIT,
} from "./appConstants";
let Filter = require("bad-words");

const _ = { get, isEmpty, map, trim, isUndefined, cloneDeep, isArray };

// Common helper file for Application

/**
 * Get current application name from environment variables
 *
 * Fallback app name is "employHer"
 */
export const getAppName = () => {
  return process.env.REACT_APP_NAME ? process.env.REACT_APP_NAME : "employHER";
};

/**
 * Get current application name from environment variables
 *
 * Fallback app name is "employHER"
 */
interface AppDomainInterface {
  protocol?: boolean;
}
export const getAppDomain = (options: AppDomainInterface = {}) => {
  const { location } = window;

  let linkParts = [];

  if (options.protocol === true) {
    linkParts.push(`${location.protocol}//`);
  }
  linkParts.push(`${location.host}/`);

  return linkParts.join("");
};

/**
 * Get API End Point
 *
 * Fallback end point is "http://localhost:8080"
 *
 * @params url Append Url after end point
 */
export const getAPIEndPoint = (url = "") => {
  const base = (
    process.env.REACT_APP_API_END_POINT
      ? process.env.REACT_APP_API_END_POINT
      : "http://localhost:8080"
  ).replace(/\/+$/, "");

  const path = url.replace(/^\/+/, "");

  return path ? `${base}/${path}` : base;
};

/**
 * Convert Object key value pair to URL query params
 *
 * @param Object Key Value pair object
 */
export const objectToParams = (object: any) => {
  Object.entries(object).map(([key, val]) => !val && delete object[key]);

  if (Object.keys(object).length > 0) {
    return new URLSearchParams(object).toString();
  } else {
    return "";
  }
};

/**
 * Get current get current tab name from company portal navigation
 *
 * @param  Object any
 * @param  Boolean true | false
 * @return Random generated integer
 */
export const getCurrentTab = (
  c: any,
  is_sub: boolean = false,
  is_module: boolean = false
) => {
  // const data = c.props ? c.props : c;
  // const path = _.get(data, "location.pathname", "");
  const path = _.get(c, "location.pathname", window.location.pathname);
  const tabs = path.split("/");
  return !_.isEmpty(tabs)
    ? is_module
      ? tabs[2]
      : is_sub
      ? tabs[4]
      : tabs[3]
    : "";
};

/**
 * Get clear string
 *
 * @param  String
 * @return cleared string without dash.
 */
export const clearString = (s: string) => {
  let string = s.toLowerCase();
  string = string.replace(/\s/g, "-");
  return string;
};

/**
 * Generate random numbers between two digits
 *
 * @param  Integer minimum value
 * @param  Integer maximum value
 * @return Random generated integer
 */
export const randomNumber = (min: number = 1, max: number = 1000) => {
  return Math.floor(Math.random() * max) + min;
};

/**
 * format the date
 */
export const formatDate = (s: string) => {
  if (!s) return "";
  const formatted = moment(s);
  return formatted.isValid() ? formatted.format("MMM D, YYYY") : "";
};

/**
 * Extract Numbers from mix string
 *
 * @param  String mix string
 * @return Pure number
 */
export const extractNumber = (str: string) => {
  if (typeof str === "string") {
    return str.match(/\d/g)?.join("");
  }
  return str;
};

export const normalizePhoneDigits = (
  value?: string | number | null
): string => String(value ?? "").replace(/\D/g, "");

/** Validates phone values from react-phone-input-2 (digits incl. country code). */
export const isValidPhoneNumber = (
  value?: string | number | null
): boolean => {
  const digits = normalizePhoneDigits(value);
  if (!digits) return false;

  // US (+1): country code plus 10-digit national number
  if (digits.startsWith("1")) {
    return digits.length === 11;
  }

  // India (+91): country code plus a 10-digit mobile number.
  // react-phone-input-2 supplies the complete value without the `+` sign.
  if (digits.startsWith("91")) {
    return /^91[6-9]\d{9}$/.test(digits);
  }

  // Legacy / national-only numbers
  return digits.length === 10;
};

export const validatePhoneNumber = (
  value?: string | number | null
): true | string => {
  const digits = normalizePhoneDigits(value);
  if (!digits) {
    return "Phone number is required";
  }
  if (!isValidPhoneNumber(value)) {
    return "Please enter a valid phone number";
  }
  return true;
};

/**
 * Retrieve a list of specific key value pair from Array
 *
 * @param Array Key Value pair `object`
 */
export const pluckFromArray = (
  data: any,
  key: any,
  valueKey: any,
  innerKey = "key",
  innerValue = "value"
) => {
  return _.map(data || [], (d, index) => {
    return {
      [innerKey]: _.isUndefined(key) ? index : d[key],
      [innerValue]: _.isUndefined(valueKey) ? d : d[valueKey],
    };
  });
};

/**
 * Retrieve a list of specific key value symbol pair from Array
 *
 * @param Array Key Value pair `object`
 */
export const pluckKeyValueSymbol = (
  data: any[],
  key?: string,
  valueKey?: string,
  symbolKey: string = "symbol",
  innerKey: string = "key",
  innerValue: string = "value",
  innerSymbol: string = "symbol"
) => {
  return _.map(data || [], (d, index) => ({
    [innerKey]: key ? d[key] : index,
    [innerValue]: valueKey ? d[valueKey] : d,
    [innerSymbol]: d[symbolKey],
  }));
};

/**
 * Sanitize a Link
 *
 * @param String proper URL
 */
export const sanitizeUrl = (url: string) => {
  let tmpUrl: string = url;

  tmpUrl = tmpUrl.replaceAll("http://", "");
  tmpUrl = tmpUrl.replaceAll("https://", "");

  return `//${tmpUrl}`;
};

/**
 * Scroll to top
 *
 */
export enum iScrollToTop {
  auto = "auto",
  smooth = "smooth",
}
export const scrollToTop = (
  scrollTo: number = 0,
  behavior: iScrollToTop = iScrollToTop.auto
): void => {
  window.scrollTo({
    top: scrollTo,
    left: scrollTo,
    behavior: behavior,
  });
};

/**
 * Merge array value with comma
 *
 * @param String with comma
 */
export const arrayGlueWith = (
  array: any[],
  glueWith: string = "and"
): string => {
  const tmpArr = _.cloneDeep(array);

  if (tmpArr.length === 1) {
    return tmpArr[0];
  }

  return `${tmpArr.splice(0, tmpArr.length - 1).join(", ")} ${glueWith} ${
    tmpArr[tmpArr.length - 1]
  }`;
};

/**
 * JSON parse with error handling
 *
 * @param String stringify JSON
 */
export const JSONParse = (str: string): object => {
  try {
    return JSON.parse(str);
  } catch (error) {
    return {};
  }
};

/**
 * Get Initial letter of full name
 *
 * @param String stringify JSON
 */
export const getInitialLetter = (
  fullName: string,
  limit: number = 2
): string => {
  let letters = "";

  fullName
    .split(" ")
    .filter((a) => a)
    .forEach((letter) => (letters = letters.concat(letter[0])));

  return letters.substr(0, limit);
};

/**
 * Generate random uuid
 *
 * @param String Generated string
 */
export const uuid = (length?: number): string => {
  const uuidLength = length || 10;
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;

  for (let i = 0; i < uuidLength; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
};

/**
 * Convert bytes to KB, MB etc...
 *
 * @param Number Bytes
 * @param Number Decimals
 */
export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

/**
 * Get time from seconds
 *
 * @param Number duration
 */
export const secondToTime = (duration: number): number => {
  var t = new Date(0);
  // t.toISOString().substr(11, 12);
  return t.setMilliseconds(1e3 * duration);
};

/**
 * Get the video meta data from Blob
 *
 * @param  Blog file
 * @return Promise
 */
export const getVideoMetaFromBlob = async (file: any) => {
  return await new Promise((resolve, reject) => {
    try {
      let video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = function () {
        resolve(this);
      };

      video.onerror = function () {
        reject("Invalid video. Please select a video file.");
      };

      video.src = window.URL.createObjectURL(file);
    } catch (e) {
      reject(e);
    }
  });
};

/**
 * Get the video meta data from Blob
 *
 * @param  Blog file
 * @return Promise
 */
export const downloadFile = (fileName: string, blob: any): void => {
  const downloadLink = document.createElement("a");
  document.body.appendChild(downloadLink);
  const url = window.URL.createObjectURL(blob);
  downloadLink.href = url;
  downloadLink.download = fileName;
  downloadLink.click();
  document.body.removeChild(downloadLink);
};

/**
 * Chat Date
 *
 * @param  Blog file
 * @return Promise
 */
export const chatDate = (dateTime: string, fullDisplay = false): string => {
  // Check if date is within a minute
  if (moment(dateTime).isSame(new Date(), "day")) {
    return moment(dateTime).format("LT");
  } else if (moment(dateTime).isSame(new Date(), "year")) {
    return fullDisplay
      ? moment(dateTime).format("MMM D, LT")
      : moment(dateTime).format("MMM D");
  } else {
    return fullDisplay
      ? moment(dateTime).format("M/D/YY LT")
      : moment(dateTime).format("MMM D, YYYY");
  }
};

export const storeNewMessageInfo = (data: any) => {
  localStorage.setItem("message", JSON.stringify(data));
};

export const fetchAndClearNewMessageInfo = () => {
  const storageData = localStorage.getItem("message") || "";
  localStorage.removeItem("message");

  return JSONParse(storageData);
};

/**
 * Open an URL in new window
 *
 * @param  String URL
 * @return Void
 */
export const openUrlInNewTab = (url: string): void => {
  const newWindow = window.open(url, "_blank", "noopener,noreferrer");
  if (newWindow) newWindow.opener = null;
};

/**
 * Extract the company slug from URL
 *
 * @return string | null
 */
export const extractCompanySlug = (): string | null => {
  const urlParts = window.location.pathname.split("/").filter((path) => path);

  if (urlParts[0] === "employer" && urlParts[0]) {
    return urlParts[1] || null;
  }

  return null;
};

/**
 * Extract first and last name from the string
 *
 * @return Object
 */
export const extractFirstLastName = (
  str: string
): { firstName: string; lastName: string } => {
  if (typeof str === "string" && str.length > 0) {
    const firstName = str.split(" ")[0];
    const lastName = str.split(" ").slice(1).join(" ");

    return { firstName, lastName };
  } else {
    return { firstName: "", lastName: "" };
  }
};

/**
 * Stripe all HTML tags from string
 *
 * @param  String HTML string
 * @return String pure string
 */
export const stripHTMLTags = (htmlString: string): string => {
  return (htmlString || "").replace(/<[^>]+>/g, "");
};

/** Convert legacy HTML content into readable plain text for plain-text fields. */
export const htmlToPlainText = (value: string | null | undefined): string => {
  if (!value) return "";

  const htmlWithBreaks = value
    .replace(/<br\s*\/?\s*>/gi, "\n")
    .replace(/<\/(?:p|div|h[1-6]|li|ul|ol)>/gi, "\n");

  if (typeof document === "undefined") {
    return stripHTMLTags(htmlWithBreaks)
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  const element = document.createElement("div");
  element.innerHTML = htmlWithBreaks;

  return (element.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]*\n[ \t]*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const applyInlineMarkdown = (value: string): string => {
  let html = escapeHtml(value);
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/__(.+?)__/g, "<strong>$1</strong>");
  html = html.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");
  return html;
};

/** Groq often returns literal "\\n" plus zero-width spaces instead of real breaks. */
export const normalizeAiText = (text: string): string =>
  String(text || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n")
    .replace(/[\u200B-\u200D\uFEFF\u00AD]/g, "")
    .replace(/\u00A0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const isBlankLine = (line: string): boolean =>
  !line.replace(/[\u200B-\u200D\uFEFF\u00AD\s]/g, "");

/** True when the string already contains HTML tags Quill can paste. */
export const isLikelyHtml = (value: string): boolean =>
  /<\/?(p|br|div|h[1-6]|ul|ol|li|strong|em|b|i|span)\b/i.test(value || "");

/**
 * Convert AI-generated plain text / markdown into Quill-safe HTML so
 * line breaks, lists, headings, and inline emphasis survive INSERT TO POST.
 */
export const aiTextToQuillHtml = (text: string): string => {
  const source = String(text || "");
  const raw = normalizeAiText(source)
    .replace(/^```(?:html|markdown|md)?\s*/i, "")
    .replace(/\s*```$/i, "")
    .trim();

  if (!raw) return "";
  if (isLikelyHtml(raw) && !/\\n/.test(source)) return raw;

  const htmlParts: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let paragraphLines: string[] = [];
  let pendingBreaks = 0;

  const flushParagraph = () => {
    if (!paragraphLines.length) return;
    const inner = paragraphLines.map(applyInlineMarkdown).join("<br/>");
    htmlParts.push(`<p>${inner}</p>`);
    paragraphLines = [];
  };

  const flushList = () => {
    if (!listType) return;
    htmlParts.push(`</${listType}>`);
    listType = null;
  };

  const applyPendingBreaks = () => {
    if (pendingBreaks > 0 && htmlParts.length) {
      htmlParts.push("<p><br/></p>");
    }
    pendingBreaks = 0;
  };

  for (const line of raw.split("\n")) {
    if (isBlankLine(line)) {
      flushParagraph();
      flushList();
      pendingBreaks += 1;
      continue;
    }

    applyPendingBreaks();

    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    const unordered = line.match(/^\s*[-*•]\s+(.+)$/);
    const ordered = line.match(/^\s*\d+[.)]\s+(.+)$/);

    if (heading) {
      flushParagraph();
      flushList();
      const level = Math.min(heading[1].length, 3);
      htmlParts.push(
        `<h${level}>${applyInlineMarkdown(heading[2])}</h${level}>`
      );
      continue;
    }

    if (unordered) {
      flushParagraph();
      if (listType !== "ul") {
        flushList();
        htmlParts.push("<ul>");
        listType = "ul";
      }
      htmlParts.push(`<li>${applyInlineMarkdown(unordered[1])}</li>`);
      continue;
    }

    if (ordered) {
      flushParagraph();
      if (listType !== "ol") {
        flushList();
        htmlParts.push("<ol>");
        listType = "ol";
      }
      htmlParts.push(`<li>${applyInlineMarkdown(ordered[1])}</li>`);
      continue;
    }

    flushList();
    paragraphLines.push(line);
  }

  flushParagraph();
  flushList();
  return htmlParts.join("");
};

/** Normalize Quill HTML for equality checks (formatting, links, etc.). */
export const normalizeRichTextHtml = (html: string): string => {
  const value = (html || "").trim();
  if (!value || value === "<p><br></p>" || value === "<p></p>") {
    return "";
  }
  return value;
};

/** True when rich text HTML differs from the original (includes formatting-only edits). */
export const hasRichTextChanged = (
  current: string,
  original: string
): boolean =>
  normalizeRichTextHtml(current) !== normalizeRichTextHtml(original);

export type GenderProfileFields = {
  gender?: string | { label?: string; value?: string | number } | null;
  gender_pronoun?: string | number | null;
  gender_optional?: string | null;
};

const normalizeGenderValue = (
  gender?: GenderProfileFields["gender"]
): string => {
  if (gender == null) return "";
  if (typeof gender === "object") {
    return String(gender.label ?? gender.value ?? "").toLowerCase();
  }
  return String(gender).toLowerCase();
};

export const GENDER_SELECT_OPTIONS = [
  { label: "Female", value: "Female" },
  { label: "They", value: "They" },
  { label: "Custom", value: "Custom" },
] as const;

/** Map stored gender (string or select object) to a react-select option. */
export const getGenderSelectOption = (
  gender?: GenderProfileFields["gender"]
): { label: string; value: string } | null => {
  const normalized = normalizeGenderValue(gender);
  if (!normalized) return null;

  return (
    GENDER_SELECT_OPTIONS.find(
      (option) => option.value.toLowerCase() === normalized
    ) ?? null
  );
};

export const isCustomGender = (
  gender?: GenderProfileFields["gender"]
): boolean => normalizeGenderValue(gender) === "custom";

/** Custom pronoun label — same logic as ProfileView `renderCustomGenderValue`. */
export const getCustomGenderPronounLabel = (
  genderPronoun?: string | number | null,
  genderOptional?: string | null
): string => {
  const pronoun = parseInt(String(genderPronoun ?? 0), 10);
  const optionalSuffix = genderOptional?.trim()
    ? `- ${genderOptional.trim()}`
    : "";

  switch (pronoun) {
    case 1:
      return `She/Her ${optionalSuffix}`.trim();
    case 2:
      return `He/Him ${optionalSuffix}`.trim();
    case 3:
      return `They/Them ${optionalSuffix}`.trim();
    default:
      return "";
  }
};

/** Display label for gender (e.g. She/Her) — same logic as ProfileView `handleGender`, without prefix. */
export const getGenderLabel = (
  gender?: GenderProfileFields["gender"],
  genderPronoun?: string | number | null,
  genderOptional?: string | null
): string => {
  switch (normalizeGenderValue(gender)) {
    case "female":
      return "She/Her";
    case "male":
      return "He/Him";
    case "they":
      return "They/Them";
    case "custom":
      return (
        getCustomGenderPronounLabel(genderPronoun, genderOptional) || "Custom"
      );
    default:
      return "";
  }
};

export const getGenderLabelFromProfile = (
  profile?: GenderProfileFields | null
): string =>
  getGenderLabel(
    profile?.gender,
    profile?.gender_pronoun,
    profile?.gender_optional
  );

/** ProfileView-style suffix appended to name: ` - She/Her` */
export const getGenderLabelSuffix = (
  profile?: GenderProfileFields | null
): string => {
  const label = getGenderLabelFromProfile(profile);
  return label ? ` - ${label}` : "";
};

/**
 * Validate a URL
 *
 * @param  String url
 * @return Boolean
 */
export const validateURL = (url: string): boolean => {
  var regex = new RegExp(
    "^(http[s]?:\\/\\/(www\\.)?|ftp:\\/\\/(www\\.)?|www\\.){1}([0-9A-Za-z-\\.@:%_+~#=]+)+((\\.[a-zA-Z]{2,3})+)(/(.)*)?(\\?(.)*)?"
  );
  return regex.test(url);
};

/**
 * Parse store data
 *
 * @param  Any store data
 * @return Store data
 */
export const parseStoreData = (store: any): any => {
  try {
    return JSON.parse(JSON.stringify(store));
  } catch (error) {
    return undefined;
  }
};

/**
 * Format number with separator
 *
 * @param  Integer
 * @param integer n: length of decimal
 * @param integer x: length of sections
 * @return Store data
 */
export const formatNumber = (
  num: number,
  n: number = 0,
  x: number = 0
): any => {
  if (typeof num === "number") {
    var re = "\\d(?=(\\d{" + (x || 3) + "})+" + (n > 0 ? "\\." : "$") + ")";
    return num.toFixed(Math.max(0, ~~n)).replace(new RegExp(re, "g"), "$&,");
  }
  return num;
};

/**
 * Count word
 *
 * @param  str String
 * @method
 * @return
 */
export const wordCount = (str: string) => {
  return str.trim().split(/\s+/).length;
};

/**
 * Remove bad words
 */
export const sanitizeBody = (body: any) => {
  const oldBody = body;
  if (_.isArray(body)) {
    body.map((element) => {
      return removeBadWords(element);
    });
  } else {
    let newBody = oldBody;
    body = removeBadWords(newBody);
  }
  return body;
};

function removeBadWords(newBody: any) {
  let elementBody = newBody;
  let customFilter = new Filter({ placeHolder: " " });
  Object.keys(elementBody).forEach((item) => {
    if (typeof elementBody[item] == "string" && elementBody[item] !== "") {
      let updateStr = customFilter.clean(elementBody[item]);
      elementBody[item] = updateStr.replace(/\s+/g, " ").trim();
    } else if (typeof elementBody[item] == "object") {
      const innerElement = elementBody[item];
      Object.keys(innerElement).forEach((itm) => {
        if (typeof innerElement[itm] == "string" && innerElement[itm] !== "") {
          let updateStr = customFilter.clean(innerElement[itm]);
          innerElement[itm] = updateStr.replace(/\s+/g, " ").trim();
        } else {
          // eslint-disable-next-line no-self-assign
          innerElement[itm] = innerElement[itm];
        }
      });
    } else {
      // eslint-disable-next-line no-self-assign
      elementBody[item] = elementBody[item];
    }
  });
  return elementBody;
}

/**
 * Find video duration from file upload only
 *
 * @param  file Blob
 * @param  duration number
 */
export const fetchVideoDuration = async (
  file: Blob | MediaSource,
  duration: number
) => {
  var video = document.createElement("video");
  video.preload = "metadata";
  video.src = URL.createObjectURL(file);

  const result = new Promise((resolve, reject) => {
    video.onloadedmetadata = function () {
      window.URL.revokeObjectURL(video.src);

      const videoDuration = Math.floor(video.duration);
      if (videoDuration > duration) {
        resolve({
          status: false,
          duration: videoDuration,
        });
      } else {
        resolve({
          status: true,
          duration: videoDuration,
        });
      }
    };
  });

  try {
    return await result;
  } catch (error) {
    return {
      status: false,
      duration: 0,
    };
  }
};

/**
 * Convert UTC time to local time
 *
 * @param {String} utcTime - UTC time string
 * @returns {String} - Local time string
 *
 */

// export const convertUTCToLocal = (utcTime: string) => {
//   if (_.isEmpty(utcTime)) return '';

//   const userLocalTime = moment.utc(utcTime).local();
//   const now = moment();

//   const duration = moment.duration(now.diff(userLocalTime));

//   const years = Math.floor(duration.asYears());
//   const months = Math.floor(duration.asMonths());
//   const weeks = Math.floor(duration.asWeeks());
//   const days = Math.floor(duration.asDays());
//   const hours = Math.floor(duration.asHours());
//   const minutes = Math.floor(duration.asMinutes());

//   if (years >= 1) {
//     return `${years} year${years > 1 ? 's' : ''}`;
//   } else if (months >= 1) {
//     return `${months} month${months > 1 ? 's' : ''}`;
//   } else if (weeks >= 1) {
//     return `${weeks} week${weeks > 1 ? 's' : ''}`;
//   } else if (days >= 1) {
//     return `${days} day${days > 1 ? 's' : ''}`;
//   } else if (hours >= 1) {
//     return `${hours} hour${hours > 1 ? 's' : ''}`;
//   } else if (minutes >= 1) {
//     return `${minutes} minute${minutes > 1 ? 's' : ''}`;
//   } else {
//     return `few seconds`;
//   }
// };

export const convertUTCToLocal = (utcTime: string) => {
  if (_.isEmpty(utcTime)) return "";

  const userLocalTime = moment.utc(utcTime).local();
  const now = moment();
  const duration = moment.duration(now.diff(userLocalTime));
  const years = Math.floor(duration.asYears());
  const months = Math.floor(duration.asMonths());
  const weeks = Math.floor(duration.asWeeks());
  const days = Math.floor(duration.asDays());
  const hours = Math.floor(duration.asHours());
  const minutes = Math.floor(duration.asMinutes());

  if (years >= 1) {
    return `${years}y `;
  } else if (months >= 1) {
    return `${months}mo `;
  } else if (weeks >= 1) {
    return `${weeks}w `;
  } else if (days >= 1) {
    return `${days}d `;
  } else if (hours >= 1) {
    return `${hours}h `;
  } else if (minutes >= 1) {
    return `${minutes}m `;
  } else {
    return `few seconds`;
  }
};

// input: "06:58 AM" in UTC
export const convertFormattedEditTime = (utcTimeString: string) => {
  if (!utcTimeString) return "";

  // Combine with today's date (or actual date if you have)
  const todayUtc = moment.utc().format("YYYY-MM-DD");
  const fullUtcTime = `${todayUtc} ${utcTimeString}`;

  // Parse in UTC and convert to local
  const localTime = moment.utc(fullUtcTime, "YYYY-MM-DD hh:mm A").local();

  // Return in desired format (e.g. "07:28 PM")
  return localTime.format("hh:mm A");
};

export const getRemainingResendHours = (
  cancelledAt?: string | Date | null,
  maxHours = RESEND_CONNECTION_REQUEST_TIME_LIMIT
) => {
  if (!cancelledAt) return maxHours;

  const cancelledAtMoment = moment.utc(
    cancelledAt,
    ["YYYY-MM-DD HH:mm:ss", moment.ISO_8601],
    true
  );
  if (!cancelledAtMoment.isValid()) return maxHours;

  const elapsedHours = moment.utc().diff(cancelledAtMoment, "hours", true);
  return Math.max(0, Math.ceil(maxHours - elapsedHours));
};

export const truncate = (text: string, limit: number) => {
  return text.length > limit ? text.slice(0, limit) + "..." : text;
};



/** Legacy ask rows that used a placeholder before empty optional notes were allowed. */
export const ASK_REQUEST_PLACEHOLDER_TEXTS =
  /^\(Recommendation request pending\)|\(Pending request\)/i;

/** Job title from candidate profile (custom or catalog). */
export const getUserDisplayJobTitle = (user: any): string => {
  const jobDetails = user?.job_details;
  const customFromDetails = String(jobDetails?.job_title_custom || "").trim();
  if (customFromDetails) return customFromDetails;
  const titleFromDetails = String(jobDetails?.title || "").trim();
  if (titleFromDetails) return titleFromDetails;

  const cp = user?.candidateProfile;
  const custom = String(cp?.job_title_custom || "").trim();
  if (custom) return custom;
  const fromProfile = String(cp?.jobTitle?.title || "").trim();
  if (fromProfile) return fromProfile;

  const profileJobTitle = String(user?.profile?.jobTitle?.title || "").trim();
  if (profileJobTitle) return profileJobTitle;
  const profileCustom = String(user?.profile?.job_title_custom || "").trim();
  if (profileCustom) return profileCustom;

  return "";
};

/** Text requester A left when asking B to recommend; skips backend placeholders. */
export const getPrefillWriteTextFromAskRequest = (rec: any): string => {
  const fromReq = String(rec?.request_recommdation_text || "").trim();
  if (fromReq) return fromReq;
  const rt = String(rec?.recommendation_text || "").trim();
  if (!rt || ASK_REQUEST_PLACEHOLDER_TEXTS.test(rt)) return "";
  return rt;
};

export const socialLoginErrorMessage = (err: unknown): string => {
  const e = err as {
    code?: string;
    message?: string;
    response?: { data?: { message?: string; data?: { message?: string } } };
  };

  const apiMsg =
    _.get(e, "response.data.message") ||
    _.get(e, "response.data.data.message");
  if (typeof apiMsg === "string" && apiMsg.trim()) {
    return apiMsg.trim();
  }

  const code = e?.code;
  if (code && FIREBASE_SOCIAL_LOGIN_MESSAGES[code]) {
    return FIREBASE_SOCIAL_LOGIN_MESSAGES[code];
  }

  const msg = e?.message;
  if (typeof msg === "string") {
    const parenMatch = msg.match(/\(auth\/[a-z0-9-]+\)/i);
    if (parenMatch) {
      const authCode = parenMatch[0].slice(1, -1);
      if (FIREBASE_SOCIAL_LOGIN_MESSAGES[authCode]) {
        return FIREBASE_SOCIAL_LOGIN_MESSAGES[authCode];
      }
      return "Sign-in could not be completed. Please try again.";
    }
    if (/network error/i.test(msg)) {
      return FIREBASE_SOCIAL_LOGIN_MESSAGES["auth/network-request-failed"];
    }
  }

  if (code?.startsWith("auth/")) {
    return (
      FIREBASE_SOCIAL_LOGIN_MESSAGES[code] ||
      "Sign-in could not be completed. Please try again."
    );
  }

  return "Social sign-in failed. Please try again.";
};

/** Default visible length for {@link maskEmailForVerificationHint} local-part prefix. */
export const MASK_EMAIL_VISIBLE_LOCAL_CHARS = 4;

/**
 * Show the first few characters of the email local part; mask the rest before @.
 */
export const maskEmailForVerificationHint = (
  email: string,
  visibleLocalChars = MASK_EMAIL_VISIBLE_LOCAL_CHARS
): string => {
  const trimmed = email.trim();
  if (!trimmed) return "";
  const at = trimmed.lastIndexOf("@");
  if (at <= 0) {
    const vis = Math.min(visibleLocalChars, trimmed.length);
    if (vis < 1) return "";
    return `${trimmed.slice(0, vis)}${"*".repeat(
      Math.max(0, trimmed.length - vis)
    )}`;
  }
  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);
  if (!domain) return trimmed;
  const vis = Math.min(visibleLocalChars, local.length);
  const prefix = local.slice(0, vis);
  const middleStars = "*".repeat(Math.max(0, local.length - vis));
  return `${prefix}${middleStars}@${domain}`;
};
